import { useEffect, useState } from 'react';
import { listAdminActivations, updateAdminActivationStatus, type AdminActivation } from '@/services/activationSites';
import type { ActivationStatus } from '@/domain/types';

const STATUS_COLORS: Record<ActivationStatus, string> = {
  active:      'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200',
  deactivated: 'bg-surface-100 text-surface-600',
  blocked:     'bg-red-50 text-red-700 ring-1 ring-red-200',
};

function displayDomain(site: AdminActivation) {
  if (site.siteHost && site.siteHost !== 'null') return site.siteHost;
  if (site.siteUrl) {
    try { return new URL(site.siteUrl).hostname; } catch { return site.siteUrl; }
  }
  return '—';
}

export default function AdminActivations() {
  const [sites, setSites]     = useState<AdminActivation[]>([]);
  const [search, setSearch]   = useState('');
  const [filter, setFilter]   = useState('all');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId]   = useState('');
  const [error, setError]     = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError('');
    listAdminActivations()
      .then((data) => { if (alive) setSites(data); })
      .catch((err) => { if (alive) setError(err?.message ?? 'Unable to load activations'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const filtered = sites.filter((s) => {
    if (filter !== 'all' && s.status !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      const domain = displayDomain(s).toLowerCase();
      if (!domain.includes(q) && !s.productName.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const handleStatusChange = async (id: string, status: ActivationStatus) => {
    try {
      setBusyId(id);
      setError('');
      await updateAdminActivationStatus(id, status);
      setSites((prev) => prev.map((s) => (s.id === id ? { ...s, status } : s)));
    } catch (err) {
      setError((err as Error)?.message ?? 'Unable to update activation');
    } finally {
      setBusyId('');
    }
  };

  const activeCount = sites.filter((s) => s.status === 'active').length;

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-surface-900">Activations</h1>
          <p className="text-sm text-surface-500 mt-1">
            Monitor and manage activated websites across all licenses.
            {!loading && (
              <span className="ml-2 font-medium text-emerald-600">{activeCount} active</span>
            )}
          </p>
        </div>
      </div>

      {error && <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-surface-400" />
          <input
            type="text"
            placeholder="Search by domain or product…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 bg-white"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {(['all', 'active', 'deactivated', 'blocked'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors ${
                filter === f ? 'bg-brand-600 text-white' : 'bg-white border border-surface-200 text-surface-600 hover:bg-surface-50'
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-surface-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-surface-200 bg-surface-50/60">
                <th className="text-left px-5 py-3 font-semibold text-surface-500 text-xs uppercase tracking-wide">Site</th>
                <th className="text-left px-5 py-3 font-semibold text-surface-500 text-xs uppercase tracking-wide">Product</th>
                <th className="text-left px-5 py-3 font-semibold text-surface-500 text-xs uppercase tracking-wide">Plugin</th>
                <th className="text-left px-5 py-3 font-semibold text-surface-500 text-xs uppercase tracking-wide">WP / PHP</th>
                <th className="text-left px-5 py-3 font-semibold text-surface-500 text-xs uppercase tracking-wide">Status</th>
                <th className="text-left px-5 py-3 font-semibold text-surface-500 text-xs uppercase tracking-wide">Last Check</th>
                <th className="text-left px-5 py-3 font-semibold text-surface-500 text-xs uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="px-5 py-10 text-center text-surface-400 text-sm">
                  <i className="ri-loader-4-line animate-spin text-xl block mb-2" />Loading activations…
                </td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={7} className="px-5 py-10 text-center text-surface-400 text-sm">
                  {sites.length === 0 ? 'No activations yet. License keys activate when installed on a WordPress site.' : 'No activations match your search.'}
                </td></tr>
              )}

              {filtered.map((site) => (
                <tr key={site.id} className="border-b border-surface-50 hover:bg-surface-50/50 transition-colors">

                  {/* Site domain + full URL */}
                  <td className="px-5 py-3">
                    <div className="font-semibold text-surface-900 text-sm">{displayDomain(site)}</div>
                    {site.siteUrl && (
                      <a
                        href={site.siteUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[10px] text-surface-400 hover:text-brand-600 transition-colors truncate max-w-[200px] block"
                      >
                        {site.siteUrl}
                      </a>
                    )}
                  </td>

                  {/* Product */}
                  <td className="px-5 py-3 text-surface-700 text-xs">{site.productName}</td>

                  {/* Plugin version */}
                  <td className="px-5 py-3 text-surface-600 text-xs">
                    {site.pluginVersion ? <span className="font-mono">v{site.pluginVersion}</span> : '—'}
                  </td>

                  {/* WP / PHP */}
                  <td className="px-5 py-3 text-surface-500 text-xs">
                    {site.wpVersion || '—'} / {site.phpVersion || '—'}
                  </td>

                  {/* Status */}
                  <td className="px-5 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${STATUS_COLORS[site.status]}`}>
                      {site.status === 'active' && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />}
                      {site.status}
                    </span>
                  </td>

                  {/* Last check */}
                  <td className="px-5 py-3 text-surface-500 text-xs">
                    {new Date(site.lastCheckAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </td>

                  {/* Actions */}
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-1">
                      {site.status === 'active' && (
                        <button
                          disabled={busyId === site.id}
                          onClick={() => handleStatusChange(site.id, 'deactivated')}
                          className="px-2.5 py-1 rounded-lg text-xs font-medium border border-surface-200 hover:bg-surface-50 disabled:opacity-40 text-surface-600 transition-colors"
                        >
                          Deactivate
                        </button>
                      )}
                      {site.status !== 'blocked' && (
                        <button
                          disabled={busyId === site.id}
                          onClick={() => handleStatusChange(site.id, 'blocked')}
                          className="px-2.5 py-1 rounded-lg text-xs font-medium hover:bg-red-50 disabled:opacity-40 text-red-600 transition-colors"
                        >
                          Block
                        </button>
                      )}
                      {site.status !== 'active' && (
                        <button
                          disabled={busyId === site.id}
                          onClick={() => handleStatusChange(site.id, 'active')}
                          className="px-2.5 py-1 rounded-lg text-xs font-medium hover:bg-emerald-50 disabled:opacity-40 text-emerald-700 transition-colors"
                        >
                          Activate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
